import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { certifications, education, recommendations, memberships } from '@/mocks/about';
import { usePageSEO } from '@/hooks/usePageSEO';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'ProfilePage',
  mainEntity: {
    '@type': 'Person',
    name: 'Jeff Dumè',
    jobTitle: 'Founder of BuildFit AI',
    description: 'Jeff Dumè is the founder of BuildFit AI and Mainframe Solutions. He helps organizations reduce mis-hires, improve retention, and create more consistent hiring decisions through human-led AI.',
    url: 'https://jeffdume.com/about',
    image: 'https://jeffdume.com/images/Jeff_D-28.jpg',
    sameAs: ['https://www.linkedin.com/in/jdume/', 'https://buildfitai.net/'],
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Houston',
      addressRegion: 'TX',
      addressCountry: 'US',
    },
    alumniOf: [
      { '@type': 'CollegeOrUniversity', name: 'Georgia Southern University' },
      { '@type': 'CollegeOrUniversity', name: 'Valdosta State University' },
    ],
    hasCredential: [
      { '@type': 'EducationalOccupationalCredential', name: 'AI Governance Executive Certification', credentialCategory: 'certificate', recognizedBy: { '@type': 'Organization', name: 'Saïd Business School, University of Oxford' }, validIn: 'US' },
      { '@type': 'EducationalOccupationalCredential', name: 'AI & Law Executive Certification', credentialCategory: 'certificate', recognizedBy: { '@type': 'Organization', name: 'Lund University' }, validIn: 'US' },
      { '@type': 'EducationalOccupationalCredential', name: 'Registered Federal Contractor', credentialCategory: 'license', recognizedBy: { '@type': 'Organization', name: 'U.S. Federal Government' }, validIn: 'US' },
    ],
  },
};

export default function About() {
  usePageSEO({
    title: 'About Jeff Dumé | Founder of BuildFit AI — Human-Led Hiring Intelligence | Houston, TX',
    description: 'Jeff Dumé is the founder of BuildFit AI and Mainframe Solutions, helping organizations reduce mis-hires, improve retention, and create more consistent hiring decisions through human-led AI. Oxford AI Governance certified. Based in Houston, TX.',
    keywords: 'Jeff Dumé biography, BuildFit AI founder, Mainframe Solutions founder, workforce intelligence, human-led AI hiring, AI governance certification, Oxford AI Governance, Lund University AI & Law, federal contractor Houston',
    canonical: '/about',
    ogTitle: 'About Jeff Dumé | Founder of BuildFit AI — Human-Led Hiring Intelligence',
    ogDescription: 'Founder of BuildFit AI and Mainframe Solutions. Helping organizations reduce mis-hires, improve retention, and create more consistent hiring decisions. AI-assisted. Never AI-decisive.',
    schema: aboutSchema,
    breadcrumbs: [{ name: 'About Jeff Dumè', item: '/about' }],
  });

  const organizationIcon = (org: string) => {
    if (org.includes('Oxford')) return 'ri-graduation-cap-line';
    if (org.includes('Lund')) return 'ri-scales-3-line';
    if (org.includes('HubSpot')) return 'ri-bar-chart-box-line';
    if (org.includes('Federal')) return 'ri-government-line';
    if (org.includes('Microsoft')) return 'ri-windows-line';
    return 'ri-award-line';
  };

  const engagementPhotos = [
    {
      src: '/images/agc-event.jpg',
      caption: 'AGC Industry Event',
      alt: 'Jeff Dumè at an AGC of Texas industry event',
    },
    {
      src: '/images/event-presence.jpg',
      caption: 'Event Presence',
      alt: 'Jeff Dumè at a professional industry event',
    },
    {
      src: '/images/federal-expo.jpg',
      caption: 'Federal Expo',
      alt: 'Jeff Dumè at a federal contracting expo',
    },
    {
      src: '/images/hca-award.jpg',
      caption: 'HCA Award',
      alt: 'Jeff Dumè receiving an HCA industry award',
    },
  ];

  return (
    <div className="bg-charcoal min-h-screen">
      <Navbar />
      <main className="pt-20 sm:pt-24 md:pt-28">
        {/* Stats Bar — above hero */}
        <section className="w-full px-4 sm:px-6 md:px-10 lg:px-16 pt-4 pb-8 md:pt-6 md:pb-12 max-w-6xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            <div className="bg-dark-card rounded-xl p-4 sm:p-5 border border-white/5 text-center">
              <p className="text-gold font-extrabold tracking-tight text-2xl sm:text-3xl" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>50+ Linkedin</p>
              <p className="text-white/40 text-xs font-medium uppercase tracking-[0.18em] mt-1.5">Recommendations</p>
            </div>
            <div className="bg-dark-card rounded-xl p-4 sm:p-5 border border-white/5 text-center">
              <p className="text-gold font-extrabold tracking-tight text-2xl sm:text-3xl" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>Certified</p>
              <p className="text-white/40 text-xs font-medium uppercase tracking-[0.18em] mt-1.5">Federal Contractor</p>
            </div>
            <div className="bg-dark-card rounded-xl p-4 sm:p-5 border border-white/5 text-center">
              <p className="text-gold font-extrabold tracking-tight text-2xl sm:text-3xl" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>Speaker</p>
              <p className="text-white/40 text-xs font-medium uppercase tracking-[0.18em] mt-1.5">HR Houston</p>
            </div>
            <div className="bg-dark-card rounded-xl p-4 sm:p-5 border border-white/5 text-center">
              <p className="text-gold font-extrabold tracking-tight text-2xl sm:text-3xl" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>Presented</p>
              <p className="text-white/40 text-xs font-medium uppercase tracking-[0.18em] mt-1.5">Gulf Coast Symposium</p>
            </div>
          </div>
        </section>

        {/* Hero — Two-Column with Portrait + Bio */}
        <section className="w-full px-4 sm:px-6 md:px-10 lg:px-16 py-12 md:py-20 lg:py-24 max-w-6xl mx-auto relative">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-32 bg-white/5 rounded-full blur-3xl pointer-events-none"></div>
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
            <div className="lg:w-[45%] w-full max-w-sm mx-auto lg:max-w-none lg:mx-0">
              <div className="relative">
                <div className="w-full aspect-[3/4] bg-dark-card rounded-xl overflow-hidden">
                  <img
                    src="/images/Jeff_D-28.jpg"
                    alt="Jeff Dumè, founder of BuildFit AI and Mainframe Solutions, workforce intelligence advocate in Houston TX"
                    loading="eager"
                    fetchPriority="high"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 w-24 h-32 sm:w-32 sm:h-40 md:w-40 md:h-52 bg-dark-card rounded-lg border border-white/10 p-2 transform rotate-3 hover:rotate-0 transition-transform duration-500 z-10">
                  <img
                    src="/images/sitting.JPG"
                    alt="Jeff Dumè in a professional setting"
                    loading="eager"
                    className="w-full h-full object-cover object-top rounded"
                  />
                </div>
                <div className="absolute -top-3 -left-3 bg-gold rounded-lg px-3 py-2 sm:px-4 sm:py-3 z-10">
                  <span className="text-charcoal text-xs font-bold uppercase tracking-wider">AI-assisted</span>
                  <span className="text-charcoal text-xs font-bold uppercase tracking-wider block mt-0.5">Never AI-decisive</span>
                </div>
              </div>
            </div>

            <div className="lg:w-[55%] w-full mt-8 lg:mt-0">
              <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3 sm:mb-4">
                About Jeff Dum&eacute;
              </p>
              <h1 className="text-white text-xl sm:text-2xl md:text-3xl lg:text-display font-bold leading-tight mb-6 sm:mb-8 font-display">
                Building Better Workforce Decisions
              </h1>

              <div className="space-y-4 sm:space-y-5 text-white/70 text-sm md:text-base leading-relaxed">
                <p>
                  Jeff Dum&eacute; is the founder of BuildFit AI&trade; and Mainframe Solutions&trade;, where he works with organizations seeking to improve hiring outcomes, strengthen operational systems, and scale with greater confidence.
                </p>
                <p>
                  Working across construction, infrastructure, engineering, healthcare, technology, and public sector organizations, Jeff kept seeing the same pattern: inconsistent evaluations, subjective hiring, competing definitions of success. Those gaps compound. They affect retention, execution, and long-term performance.
                </p>
                <p>
                  That pattern led to BuildFit AI&trade;, a platform that structures how organizations evaluate talent, reducing mis-hires, improving retention, and making hiring decisions more consistent.
                </p>
                <p>
                  He holds executive certifications in AI Governance from Oxford&rsquo;s Sa&iuml;d Business School and AI &amp; Law from Lund University.
                </p>
                <p>
                  He believes organizations grow when people, systems, and decisions are aligned.
                </p>
                <p>
                  He believes operational discipline is the foundation of confident growth.
                </p>
              </div>

              <div className="mt-8 sm:mt-10">
                <a
                  href="https://www.linkedin.com/in/jdume/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-gold text-sm font-semibold hover:underline"
                >
                  View on LinkedIn
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Leadership & Industry Engagement */}
        <section className="w-full bg-charcoal py-12 md:py-20 lg:py-24 border-t border-white/5 relative overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-24 bg-white/4 rounded-full blur-3xl pointer-events-none"></div>
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3 sm:mb-4">
              Leadership &amp; Industry Engagement
            </p>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4 mb-8 sm:mb-10">
              <h2 className="text-white text-xl sm:text-2xl md:text-3xl font-bold leading-tight font-display">
                Building Relationships That Last
              </h2>
              <p className="text-white/50 text-sm leading-relaxed max-w-md">
                The work happens through partnerships, conferences, speaking engagements, and continuous learning. These moments represent the relationships and communities that shape long-term growth.
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
              {engagementPhotos.map((photo, idx) => (
                <div
                  key={idx}
                  className="group relative rounded-xl overflow-hidden bg-dark-card aspect-[4/3] cursor-pointer"
                >
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-2 sm:p-4">
                    <p className="text-white font-semibold text-xs sm:text-sm">{photo.caption}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Education & Professional Development */}
        <section className="w-full bg-charcoal py-12 md:py-20 lg:py-24 border-t border-white/5 relative overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-20 bg-white/4 rounded-full blur-3xl pointer-events-none"></div>
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-5xl mx-auto">
            <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3 sm:mb-4">
              Education &amp; Professional Development
            </p>
            <h2 className="text-white text-xl sm:text-2xl md:text-3xl font-bold leading-tight mb-8 sm:mb-10">
              Certifications &amp; Credentials
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4 sm:gap-5">
              {certifications.map((cert) => (
                <div
                  key={cert.id}
                  className="group bg-dark-card rounded-xl p-5 sm:p-6 border border-white/5 hover:border-gold/20 transition-colors flex flex-col min-h-[210px]"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg bg-white/5 text-white/50 group-hover:bg-gold/10 group-hover:text-gold transition-colors shrink-0">
                      <i className={`${organizationIcon(cert.organization)} text-lg`}></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-white font-semibold text-sm leading-tight">
                        {cert.title}
                      </p>
                      <p className="text-white/40 text-xs mt-0.5 truncate">
                        {cert.organization}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2 text-xs text-white/50 flex-1">
                    <div className="flex items-center gap-2">
                      <i className="ri-calendar-line text-white/30 shrink-0"></i>
                      <span>
                        Issued {cert.issued}
                        {cert.expires && ` · Expires ${cert.expires}`}
                      </span>
                    </div>
                    {cert.credentialId && (
                      <div className="flex items-center gap-2">
                        <i className="ri-shield-keyhole-line text-white/30 shrink-0"></i>
                        <span className="font-mono truncate">ID: {cert.credentialId}</span>
                      </div>
                    )}
                    <div className="flex items-start gap-2">
                      <i className="ri-lightbulb-line text-white/30 shrink-0 mt-0.5"></i>
                      <span className="leading-relaxed line-clamp-2">{cert.skills}</span>
                    </div>
                  </div>

                  <a
                    href={cert.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-4 text-gold text-xs font-semibold hover:underline"
                  >
                    Show credential
                    <i className="ri-arrow-right-up-line"></i>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Professional Organizations */}
        <section className="w-full px-4 sm:px-6 md:px-10 lg:px-16 py-12 md:py-20 lg:py-24 max-w-5xl mx-auto border-t border-white/5 relative">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-16 bg-white/4 rounded-full blur-3xl pointer-events-none"></div>
          <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3 sm:mb-4">
            Professional Organizations
          </p>
          <h2 className="text-white text-xl sm:text-2xl md:text-3xl font-bold leading-tight mb-8 sm:mb-10">
            Memberships &amp; Associations
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            {memberships.map((member) => (
              <div
                key={member.id}
                className="flex items-start gap-3 sm:gap-4 bg-dark-card rounded-xl p-4 sm:p-6 border border-white/5 hover:border-gold/20 transition-colors"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/5 text-white/50 shrink-0">
                  <i className="ri-building-line text-lg"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm leading-tight">
                    {member.name}
                  </p>
                  <p className="text-white/40 text-xs mt-1">
                    {member.role} &middot; {member.period}
                  </p>
                  <p className="text-white/30 text-xs mt-1">
                    Associated with {member.association}
                  </p>
                  {'description' in member && member.description && (
                    <p className="text-white/40 text-xs mt-2 leading-relaxed">
                      {member.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Education */}
        <section className="w-full bg-charcoal py-12 md:py-20 lg:py-24 border-t border-white/5 relative overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-20 bg-white/4 rounded-full blur-3xl pointer-events-none"></div>
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-5xl mx-auto">
            <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3 sm:mb-4">
              Academic Foundation
            </p>
            <h2 className="text-white text-xl sm:text-2xl md:text-3xl font-bold leading-tight mb-8 sm:mb-10">
              Education
            </h2>

            <div className="space-y-4 sm:space-y-6">
              {education.map((edu) => (
                <div
                  key={edu.id}
                  className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-6 bg-dark-card rounded-xl p-4 sm:p-6 border border-white/5"
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-xl bg-white/5 text-white/50 shrink-0">
                    <i className="ri-graduation-cap-line text-xl"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-3">
                      <h3 className="text-white font-semibold text-sm sm:text-base">{edu.institution}</h3>
                      <span className="text-white/35 text-xs shrink-0">{edu.period}</span>
                    </div>
                    <p className="text-white/60 text-sm mt-1">
                      {edu.degree}, {edu.field}
                    </p>
                    {edu.notes && (
                      <p className="text-white/40 text-xs mt-2">{edu.notes}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 50+ LinkedIn Recommendations */}
        <section className="w-full px-4 sm:px-6 md:px-10 lg:px-16 py-12 md:py-16 lg:py-20 max-w-7xl mx-auto relative border-t border-white/5">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-32 bg-white/5 rounded-full blur-3xl pointer-events-none"></div>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8 sm:mb-10">
            <div>
              <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3">
                Social Proof
              </p>
              <h2 className="text-white text-xl sm:text-2xl md:text-3xl font-bold leading-tight font-display">
                50+ LinkedIn Recommendations
              </h2>
            </div>
            <a
              href="https://www.linkedin.com/in/jdume/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-gold text-sm font-semibold hover:underline whitespace-nowrap"
            >
              View all on LinkedIn
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {recommendations.map((rec) => (
              <div
                key={rec.id}
                className="bg-dark-card rounded-xl p-5 sm:p-6 border border-white/5 hover:border-gold/20 transition-colors flex flex-col group"
              >
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-gold/15 flex items-center justify-center shrink-0">
                    <span className="text-gold font-bold text-sm">
                      {rec.name.charAt(0)}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-white font-semibold text-sm leading-tight">{rec.name}</p>
                    <p className="text-white/40 text-xs leading-snug mt-0.5">{rec.role}</p>
                  </div>
                </div>
                <p className="text-white/35 text-xs mb-3">{rec.relation}</p>
                <p className="text-white/60 text-sm leading-relaxed flex-1 line-clamp-6 group-hover:line-clamp-none transition-all duration-300">
                  &ldquo;{rec.endorsement}&rdquo;
                </p>
              </div>
            ))}
          </div>
        </section>


      </main>
      <Footer />
    </div>
  );
}